import type { JSX, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { ClientsApi } from './api';

/**
 * What every `/clients` screen needs from outside React, plus rule 14's one decision.
 *
 * `canReadFull` mirrors the api's masking gate so a screen can say *why* a number is
 * masked ("your role sees the number masked") instead of leaving a blank. It is never the
 * control: `ClientMaskedOut` has no `phone_e164` property at all, so the full number is
 * not in the response whatever this flag says (rule 14).
 */
export interface ClientsDeps {
  api: ClientsApi;
  /** `client.read_full` — owner, manager, frontdesk, accountant (rule 14). */
  canReadFull: boolean;
  /** `client.write` — owner, manager, frontdesk. */
  canWrite: boolean;
  /**
   * `client.write` **or** `session.run` — the nail profile only.
   *
   * Rule 14 gives the nail-profile PUT a wider gate than the rest of the client
   * record ("the route checks `client.write OR session.run`; see 14.9") so a
   * technician at the chair can write down what they have just learned about
   * somebody's nails. `api/app/modules/clients/router.py` has it as
   * `WriteProfile = require_any(PERM_CLIENT_WRITE, PERM_SESSION_RUN)`; this is
   * the screen's half of the same rule, and it was `canWrite` until BP-03's
   * technician journey first ran and found the editor disabled for the one role
   * 14.9 is about.
   */
  canWriteProfile: boolean;
  /** `client.merge` — owner, manager. */
  canMerge: boolean;
}

const ClientsDepsContext = createContext<ClientsDeps | null>(null);

/** Make `deps` available to every `/clients` screen below. */
export function ClientsDepsProvider({
  deps,
  children,
}: {
  deps: ClientsDeps;
  children: ReactNode;
}): JSX.Element {
  return <ClientsDepsContext.Provider value={deps}>{children}</ClientsDepsContext.Provider>;
}

/** The injected dependencies. Throws when a screen is rendered outside the provider. */
export function useClientsDeps(): ClientsDeps {
  const deps = useContext(ClientsDepsContext);
  if (deps === null) {
    throw new Error('26AD clients: this screen must be rendered inside <ClientsDepsProvider>');
  }
  return deps;
}
